// Past readings from a keeper's own spreadsheet (feedback #67, 2026-10-07:
// "allow previous measured parameters to be added from a spreadsheet").
// Pure, shared by the app (preview, column matching, units) and the server
// (every row checked again before it is saved).
//
// Rules, from docs/ANTI-PATTERNS.md and the owner's decisions:
//   - a unit is never guessed silently: each column's unit is suggested and
//     the keeper confirms it (anti-pattern 17, the 1000x phosphate bug);
//   - a value no kit can read is refused and named, never saved or "fixed";
//   - a date that reads two ways (03/04/2026) is asked, never assumed;
//   - an import never alerts, flags outliers or ticks off tasks (the
//     server's job), and can be removed as a whole.

import { implausibleReading } from './reading-plausibility.js';
import { FRESHWATER_SUBTYPES } from './param-explainers.js';

export const IMPORT_MAX_ROWS = 5000;
// No reef log goes back further; a typo'd 1926 is refused, not charted.
export const IMPORT_EARLIEST = Date.UTC(2000, 0, 1);

const PPT_PER_SG = 35 / 0.026;
const round = (n, dp) => Math.round(n * 10 ** dp) / 10 ** dp;

// ─── Reading pasted or file text ───────────────────────────────────

// A paste from Google Sheets or Excel is tab-separated; a CSV file uses
// commas, or semicolons where the decimal mark is a comma. Quotes are read
// across line breaks (a Notes cell with a new line in it stays one cell),
// and blank lines are kept so row numbers match the keeper's sheet.
export function parseDelimited(text) {
  const src = String(text || '').replace(/^\uFEFF/, '');
  // The separator comes from the first line whose separator splits the
  // next line the same way: a title above the table ("My reef log" or "My
  // reef log, 2026") is skipped. Not the busiest line, since a semicolon
  // file's decimal commas would outvote its semicolons.
  const lines = src.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (!lines.length) return [];
  const count = (line, ch) => line.split(ch).length - 1;
  const sepOf = (line) =>
    count(line, '\t') ? '\t' : count(line, ';') > count(line, ',') ? ';' : ',';
  const head = lines.slice(0, 15);
  const at = head.findIndex(
    (l, i) =>
      /[\t;,]/.test(l) && i + 1 < head.length && count(head[i + 1], sepOf(l)) === count(l, sepOf(l))
  );
  const firstLine = at >= 0 ? head[at] : head.find((l) => /[\t;,]/.test(l)) || head[0];
  const sep = sepOf(firstLine);
  const rows = [];
  let row = [];
  let cur = '';
  // Whether the cell has anything but spaces yet. Asked of cur.trim() on
  // every quote, a cell like a"a"a"... took 26 s at 320 KB.
  let blank = true;
  let quoted = false;
  for (let i = 0; i < src.length; i += 1) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') {
        cur += '"';
        blank = false;
        i += 1;
      } else if (c === '"') quoted = false;
      else {
        cur += c;
        if (blank && c.trim() !== '') blank = false;
      }
    } else if (c === '"' && blank) {
      quoted = true;
      cur = '';
    } else if (c === sep) {
      row.push(cur.trim());
      cur = '';
      blank = true;
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cur.trim());
      rows.push(row);
      row = [];
      cur = '';
      blank = true;
    } else {
      cur += c;
      if (blank && c.trim() !== '') blank = false;
    }
  }
  if (cur !== '' || row.length) {
    row.push(cur.trim());
    rows.push(row);
  }
  while (rows.length && rows[rows.length - 1].every((c) => c === '')) rows.pop();
  return rows;
}

// ─── Columns ────────────────────────────────────────────────────────

const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/°/g, ' deg ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Names first, then symbols, so "Calcium (mg/l)" is calcium and not
// magnesium. Phosphorus is NOT phosphate (x3.066): it is matched to
// phosphate only with its own unit, "ppb phosphorus".
const NAME_RULES = [
  ['calcium', /\b(calcium|kalzium|calcio)\b/],
  ['magnesium', /\b(magnesium|magnesio)\b/],
  ['nitrate', /\b(nitrate|nitrat|nitrato)\b/],
  ['nitrite', /\b(nitrite|nitrit|nitrito)\b/],
  ['ammonia', /\b(ammonia|ammonium|ammoniak|amoniaco)\b/],
  ['phosphate', /\b(phosphate|phosphat|fosfato)\b/],
  ['phosphate', /\b(phosphorus|phosphor)\b/],
  ['salinity', /\b(salinity|salinitat|salinidad|salinita|specific gravity|spec gravity|dichte)\b/],
  ['temp', /\b(temp|temperature|temperatur|temperatura)\b/],
  ['alk', /\b(alk|alkalinity|alkalinitat|alcalinidad)\b/],
  ['ph', /\bph\b/],
];
// Magnesium last: "mg" is also a unit, so "PO4 (mg)" or "NO3 mg" is the
// other symbol, never magnesium.
const SYMBOL_RULES = [
  ['calcium', /\bca\b/],
  ['nitrate', /\bno3\b/],
  ['nitrite', /\bno2\b/],
  ['ammonia', /\b(nh3|nh4)\b/],
  ['phosphate', /\bpo4\b/],
  ['salinity', /\b(sal|sg|s g|psu|ppt)\b/],
  ['alk', /\b(kh|dkh)\b/],
  ['magnesium', /\bmg\b/],
];

const isDateHeader = (h) => /\b(date|day|time|when|tested|datum)\b/.test(norm(h));
const isTimeHeader = (h) => /^(time|time tested|test time|time of day|uhrzeit|zeit)$/.test(norm(h));

// `params` is the tank's own cards ({ key: { label, unit } }). Only a
// parameter with a card can take readings; anything else is skipped and
// says why.
export function matchParam(header, params = {}) {
  const h = norm(header);
  if (!h) return null;
  // The keeper's own cards by name or key first ("Iodine", "potassium").
  for (const [key, card] of Object.entries(params)) {
    if (!card) continue;
    if (h === norm(card.label) || h === norm(key)) return key;
  }
  const withoutUnits = h.replace(/\bmg l\b|\bmg per l\b/g, ' ');
  const hit =
    NAME_RULES.find(([, re]) => re.test(h)) || SYMBOL_RULES.find(([, re]) => re.test(withoutUnits));
  if (!hit) return null;
  let key = hit[0];
  // A freshwater tank's carbonate hardness card is `kh`, not alkalinity.
  if (key === 'alk' && !params.alk && params.kh) key = 'kh';
  return params[key] ? key : null;
}

// ─── Units ──────────────────────────────────────────────────────────
// Storage: alk dKH, phosphate ppm, temperature °F, salinity ppt. Factors:
// 1 meq/L = 2.8 dKH = 50 ppm as CaCO3 (Holmes-Farley, "The Units of
// Measure of Reefkeeping", Reefkeeping 2005); PO4 = P x 3.066 (the molar
// mass ratio the Hanna Phosphorus checker note in params.js uses).

export const UNIT_OPTIONS = {
  alk: [
    { k: 'dkh', label: 'dKH', to: (v) => v },
    { k: 'meq', label: 'meq/L', to: (v) => round(v * 2.8, 2) },
    { k: 'ppm', label: 'ppm (as CaCO3)', to: (v) => round((v / 50) * 2.8, 2) },
  ],
  phosphate: [
    { k: 'ppm', label: 'ppm', to: (v) => v },
    // Six places, so a ppm column read as ppb never rounds to a believable
    // 0 and slips past the far-out hold-back (import-review.js).
    { k: 'ppb', label: 'ppb', to: (v) => round(v / 1000, 6) },
    {
      k: 'ppb_p',
      label: 'ppb phosphorus (Hanna Phosphorus checker)',
      to: (v) => round((v * 3.066) / 1000, 6),
    },
    { k: 'ppm_p', label: 'ppm phosphorus (P)', to: (v) => round(v * 3.066, 6) },
  ],
  // NO3 = NO3-N x 4.43 (molar mass ratio, 62.0 / 14.0).
  nitrate: [
    { k: 'ppm', label: 'ppm', to: (v) => v },
    { k: 'n', label: 'ppm nitrate-nitrogen (NO3-N)', to: (v) => round(v * 4.43, 2) },
  ],
  // Freshwater hardness: 1 dKH = 1 dGH = 17.86 ppm as CaCO3.
  kh: [
    { k: 'dkh', label: 'dKH', to: (v) => v },
    { k: 'ppm', label: 'ppm (as CaCO3)', to: (v) => round(v / 17.86, 1) },
  ],
  gh: [
    { k: 'dgh', label: 'dGH', to: (v) => v },
    { k: 'ppm', label: 'ppm (as CaCO3)', to: (v) => round(v / 17.86, 1) },
  ],
  temp: [
    { k: 'f', label: '°F', to: (v) => v },
    { k: 'c', label: '°C', to: (v) => round((v * 9) / 5 + 32, 1) },
  ],
  // Read from each number: an S.G. lives below 2 (app-src/src/salinity.js).
  salinity: [
    {
      k: 'auto',
      label: 'ppt, PSU or S.G. (read from each number)',
      to: (v) => (v > 0.9 && v < 2 ? round((v - 1) * PPT_PER_SG, 1) : round(v, 1)),
    },
  ],
};

export function unitOptionsFor(param) {
  return UNIT_OPTIONS[param] || null;
}

// Both middles of the column (the same number for an odd count). A unit
// other than the card's own is suggested only when both agree, so in a
// short column one typo (86.8 beside 8.1, 0.84 beside 8.4) never picks the
// unit for the rest.
const middles = (xs) => {
  const s = xs.filter(Number.isFinite).sort((a, b) => a - b);
  return s.length ? [s[Math.floor((s.length - 1) / 2)], s[Math.floor(s.length / 2)]] : null;
};

// A suggestion the keeper confirms, never a silent choice: the header's own
// words first, then what the numbers look like.
export function suggestUnit(param, header, values = []) {
  const opts = UNIT_OPTIONS[param];
  if (!opts) return null;
  const h = norm(header);
  const mid = middles(values.map(parseNumber).filter((x) => x != null));
  const both = (test) => !!mid && test(mid[0]) && test(mid[1]);
  if (param === 'alk') {
    if (/\bmeq\b/.test(h)) return 'meq';
    if (/\b(ppm|mg l|caco3)\b/.test(h)) return 'ppm';
    if (/\bdkh\b/.test(h)) return 'dkh';
    if (both((m) => m < 5)) return 'meq';
    if (both((m) => m > 40)) return 'ppm';
    return 'dkh';
  }
  if (param === 'phosphate') {
    // Hanna's HI736 Phosphorus checker reads ppb of phosphorus, so "HI736
    // ppb" is phosphorus too. Only a header already in ppm or converted
    // overrides it: "PO4 ppm (HI736 converted)" is ppm (review, 2026-10-08:
    // it imported 20,000x low, and "HI736 ppb" as phosphate 3x low).
    if (/\bhi ?736\b/.test(h) && !/\b(ppm|mg l|mg|convert\w*)\b/.test(h)) return 'ppb_p';
    if (/\b(phosphorus|phosphor)\b/.test(h) || /^p\b/.test(h)) {
      if (/\b(ppm|mg l)\b/.test(h)) return 'ppm_p';
      if (/\b(ppb|ug l|ug)\b/.test(h)) return 'ppb_p';
      return both((m) => m < 3) ? 'ppm_p' : 'ppb_p';
    }
    if (/\b(ppb|ug l|ug)\b/.test(h)) return 'ppb';
    if (/\b(ppm|mg l|mg)\b/.test(h)) return 'ppm';
    return both((m) => m > 3) ? 'ppb' : 'ppm';
  }
  if (param === 'temp') {
    if (/\b(deg c|c)\b/.test(h) || /celsius/.test(h)) return 'c';
    if (/\b(deg f|f)\b/.test(h) || /fahrenheit/.test(h)) return 'f';
    return both((m) => m < 45) ? 'c' : 'f';
  }
  if (param === 'nitrate')
    return /\b(no3 n|n|nitrogen)\b/.test(h.replace(/\bnitrate\b/, '')) ? 'n' : 'ppm';
  if (param === 'kh' || param === 'gh') {
    if (/\b(ppm|mg l|caco3)\b/.test(h)) return 'ppm';
    return both((m) => m > 30) ? 'ppm' : opts[0].k;
  }
  return opts[0].k;
}

// ─── Numbers ────────────────────────────────────────────────────────

// "8.2", "8,2", "8.2 dKH" -> 8.2. "<0.02", "ND" and "-" are not numbers:
// below-detection is not a reading.
// `thousands` reads "1,350" or "1.350" as 1350: set for a column whose
// numbers are never below 10 (calcium, magnesium, ppb), where 1.35 could
// only be a misread.
export function parseNumber(cell, { thousands = false } = {}) {
  const s = String(cell ?? '').trim();
  if (!s || /^[<>]/.test(s)) return null;
  if (thousands && /^\d{1,3}([.,]\d{3})+$/.test(s)) return Number(s.replace(/[.,]/g, ''));
  const m = s.match(/^([+-]?\d+(?:[.,]\d+)?(?:[eE][+-]?\d+)?)(?:\s*[a-zA-Z°/%µ ]*)$/);
  if (!m) return null;
  const n = Number(m[1].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

const THOUSANDS_UNITS = new Set(['ppb', 'ppb_p']);
const THOUSANDS_PARAMS = new Set(['calcium', 'magnesium']);

const isBlank = (cell) => /^(|-|—|n\/?a|na|none|nt|not tested)$/i.test(String(cell ?? '').trim());

// ─── Dates ──────────────────────────────────────────────────────────

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

function timeOf(rest) {
  const t = String(rest || '').match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!t) return null;
  let h = Number(t[1]);
  const ap = (t[4] || '').toLowerCase();
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return { h, mi: Number(t[2]) };
}

// Local time: a test logged at the tank on a date. No time given reads as
// local noon, so the date never slides a day across time zones.
// Today's date with no time, imported in the morning, is not "in the
// future": it reads as now instead of noon.
function local(y, mo, d, t) {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, mo - 1, d, t ? t.h : 12, t ? t.mi : 0, 0, 0);
  if (date.getMonth() !== mo - 1 || date.getDate() !== d) return null;
  if (!t) {
    const now = new Date();
    if (date > now && date.toDateString() === now.toDateString()) return now;
  }
  return date;
}

const fullYear = (y) => (y < 100 ? 2000 + y : y);

const WEEKDAY_PREFIX = /^(mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?,?\s+/i;
// "Tuesday, 07/10/2026" -> "07/10/2026": the weekday adds nothing.
export const withoutWeekday = (cell) =>
  String(cell ?? '')
    .trim()
    .replace(WEEKDAY_PREFIX, '');

// Which way the slashed dates in a column read: 'dmy', 'mdy', 'ambiguous'
// (every day and month 12 or under: the keeper is asked), 'mixed', or
// 'none' when no date in the column is slashed (ISO, "7 Oct 2026").
export function detectDateOrder(cells) {
  let dayFirst = false;
  let monthFirst = false;
  let seen = false;
  for (const c of cells) {
    // The weekday is dropped as parseDateCell drops it: "Tue 07/10/2026"
    // read as no slashed date at all, so the order was never asked.
    const m = withoutWeekday(c).match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/);
    if (!m) continue;
    seen = true;
    if (Number(m[1]) > 12) dayFirst = true;
    if (Number(m[2]) > 12) monthFirst = true;
  }
  if (dayFirst && monthFirst) return 'mixed';
  if (dayFirst) return 'dmy';
  if (monthFirst) return 'mdy';
  return seen ? 'ambiguous' : 'none';
}

// A Date, or null when the cell is not a date. `order` is 'dmy' or 'mdy'
// for slashed dates.
// `date1904`: the workbook counts Excel day numbers from 1904 (an old Mac
// Excel file), 1462 days later than the usual 1900 system.
export function parseDateCell(cell, order = 'mdy', { date1904 = false } = {}) {
  // "Tuesday, October 7, 2026", "Tue 7 Oct 2026": the weekday adds nothing.
  const s = withoutWeekday(cell);
  if (!s) return null;
  // A full timestamp with its zone ("...T20:30:00Z", "+02:00") is an instant.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    const t = Date.parse(s);
    return Number.isFinite(t) ? new Date(t) : null;
  }
  // An Excel date arrives as a day count from 1899-12-30.
  if (/^\d{5}(\.\d+)?$/.test(s)) {
    const n = Number(s) + (date1904 ? 1462 : 0);
    if (n < 30000 || n > 80000) return null;
    const ms = Date.UTC(1899, 11, 30) + Math.round(n * 86400000);
    const u = new Date(ms);
    const hasTime = n % 1 !== 0;
    return local(
      u.getUTCFullYear(),
      u.getUTCMonth() + 1,
      u.getUTCDate(),
      hasTime ? { h: u.getUTCHours(), mi: u.getUTCMinutes() } : null
    );
  }
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(.*)$/);
  if (m) return local(Number(m[1]), Number(m[2]), Number(m[3]), timeOf(m[4]));
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})(.*)$/);
  if (m) {
    const [a, b] = [Number(m[1]), Number(m[2])];
    const [d, mo] = order === 'dmy' ? [a, b] : [b, a];
    return local(fullYear(Number(m[3])), mo, d, timeOf(m[4]));
  }
  // "7 Oct 2026", "Oct 7, 2026", "7-Oct-26"
  m = s.match(/^(\d{1,2})[\s-]+([a-z]{3,})[\s,-]+(\d{2,4})(.*)$/i);
  if (m) {
    const mo = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1;
    return mo ? local(fullYear(Number(m[3])), mo, Number(m[1]), timeOf(m[4])) : null;
  }
  m = s.match(/^([a-z]{3,})\s+(\d{1,2}),?\s+(\d{2,4})(.*)$/i);
  if (m) {
    const mo = MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) + 1;
    return mo ? local(fullYear(Number(m[3])), mo, Number(m[2]), timeOf(m[4])) : null;
  }
  return null;
}

// ─── The table, read ────────────────────────────────────────────────

const isLongHeader = (header) => {
  const hs = header.map(norm);
  return (
    hs.some((h) => /\b(parameter|param|test|element)\b/.test(h)) &&
    hs.some((h) => /\b(value|result|reading)\b/.test(h))
  );
};

// A long sheet (date, parameter, value[, unit][, time]) becomes the usual
// wide one: one column per parameter name, one row per date and time.
// `cellLines` keeps the keeper's own row number for every cell, so a
// refused cell is named by the row in their sheet, not the pivoted one.
// `headerLine` is the sheet's row number of the heading row.
export function widen(table, { headerLine = 1 } = {}) {
  const [header, ...rows] = table;
  if (!header || !isLongHeader(header)) return table;
  const hs = header.map(norm);
  let di = hs.findIndex((h) => isDateHeader(h) && !isTimeHeader(h));
  let ti = hs.findIndex((h, k) => k !== di && isTimeHeader(h));
  // A sheet whose only date column is headed "Time" still has its dates.
  if (di < 0 && ti >= 0) [di, ti] = [ti, -1];
  // "Test time" is the time, not the test: the date and time columns are
  // never taken as the parameter, value or unit.
  const pick = (re) => hs.findIndex((h, k) => k !== di && k !== ti && re.test(h));
  const pi = pick(/\b(parameter|param|test|element)\b/);
  const vi = pick(/\b(value|result|reading)\b/);
  const ui = pick(/\bunits?\b/);
  if (di < 0 || pi < 0 || vi < 0) return table;
  const names = [];
  const nameOf = (r) => {
    const unit = ui >= 0 && r[ui] ? ` (${r[ui]})` : '';
    return `${r[pi] || ''}${unit}`.trim();
  };
  for (const r of rows) {
    const n = nameOf(r);
    if (n && !names.includes(n)) names.push(n);
  }
  const lead = ti >= 0 ? 2 : 1;
  // One row per date (and time), so a day's tests sit together as in a
  // wide sheet. A second value for the same test then starts a new row.
  const out = [[header[di] || 'Date', ...(ti >= 0 ? [header[ti] || 'Time'] : []), ...names]];
  const cellLines = [[]];
  const open = new Map();
  rows.forEach((r, idx) => {
    const i = names.indexOf(nameOf(r));
    if (i < 0) return;
    const date = r[di] || '';
    const time = ti >= 0 ? r[ti] || '' : '';
    const key = `${date}\u0000${time}`;
    let at = open.get(key);
    if (at == null || out[at][i + lead] !== '') {
      const line = new Array(names.length + lead).fill('');
      line[0] = date;
      if (ti >= 0) line[1] = time;
      out.push(line);
      cellLines.push([]);
      at = out.length - 1;
      open.set(key, at);
    }
    out[at][i + lead] = r[vi] ?? '';
    cellLines[at][i + lead] = headerLine + idx + 1;
  });
  // Not enumerable: the table itself stays a plain array of rows.
  Object.defineProperty(out, 'cellLines', { value: cellLines });
  return out;
}

// A separate Time column joins its date: "20:30", "8:30 pm", or Excel's
// fraction of a day (0.854).
function withTime(dateCell, timeCell, date1904 = false) {
  const t = String(timeCell ?? '').trim();
  if (!t) return dateCell;
  const d = String(dateCell ?? '').trim();
  if (/^0?\.\d+$/.test(t)) {
    const mins = Math.round(Number(t) * 1440);
    const hhmm = `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
    return /^\d{5}$/.test(d) ? String(Number(d) + Number(t)) : `${d} ${hhmm}`;
  }
  if (/^\d{5}$/.test(d)) {
    const day = parseDateCell(d, 'mdy', { date1904 });
    if (!day) return d;
    const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    return `${iso} ${t}`;
  }
  return `${d} ${t}`;
}

// What each column should be, as suggestions the keeper can change:
// [{ kind: 'date' | 'time' | 'param' | 'skip', param, unit, header }].
export function suggestColumns(table, params = {}) {
  const [header = [], ...rows] = table;
  let dateTaken = false;
  return header.map((h, i) => {
    const values = rows.map((r) => r[i]).filter((v) => !isBlank(v));
    if (
      !dateTaken &&
      (isDateHeader(h) || (values.length && values.every((v) => parseDateCell(v))))
    ) {
      dateTaken = true;
      return { kind: 'date', header: h };
    }
    if (dateTaken && isTimeHeader(h)) return { kind: 'time', header: h };
    const param = matchParam(h, params);
    if (!param) return { kind: 'skip', header: h };
    return { kind: 'param', param, unit: suggestUnit(param, h, values), header: h };
  });
}

// One reading's checks, run by the app for the preview and again by the
// server before saving. null when it may be saved, else the keeper's words.
// `shown` words a refusal in what the keeper typed ("77°C", "1.25").
export function readingProblem(
  { param, value, ts },
  { params, subtypeSlug, now = Date.now(), shown = null }
) {
  if (!params || !Object.hasOwn(params, param) || !params[param])
    return 'This tank has no card for it.';
  if (params[param].hidden) return 'This card is hidden on Home. Show it in Parameters first.';
  if (!Number.isFinite(value)) return 'Not a number.';
  const t = Date.parse(ts);
  if (!Number.isFinite(t)) return 'The date could not be read.';
  if (t > now + 5 * 60 * 1000) return 'The date is in the future.';
  if (t < IMPORT_EARLIEST) return 'The date is before 2000.';
  const wrong = implausibleReading(param, value, { subtypeSlug, shown });
  return wrong ? wrong.message : null;
}

// The readings a mapped table holds: { rows: [{ param, value, ts, line }],
// refused: [{ line, header, cell, reason }] }. `line` is the sheet's own
// row number (1 is the heading row).
// Which row holds the headings: a title row ("My reef log") or a blank row
// above them is common. The first of the top 15 rows naming a date or two
// of the tank's tests; else the first row.
export function findHeaderRow(table, params = {}) {
  // Fifteen rows with something in them: blank rows (kept so row numbers
  // match the sheet) do not count against the search.
  let seen = 0;
  for (let i = 0; i < table.length && seen < 15; i += 1) {
    const row = table[i] || [];
    if (!row.some((c) => String(c ?? '').trim() !== '')) continue;
    seen += 1;
    const tests = row.filter((h) => matchParam(h, params)).length;
    if (row.some((h) => isDateHeader(h)) || tests >= 2) return i;
  }
  // Else the first row with anything in it: blank rows above are kept so
  // row numbers match the sheet.
  const first = table.findIndex((r) => (r || []).some((c) => String(c ?? '').trim() !== ''));
  return first < 0 ? 0 : first;
}

const hasTimeOfDay = (s) => /\d:\d{2}/.test(s) || /^\d{5}\.\d+$/.test(s) || /T\d{2}:/.test(s);

// `headerLine` is the sheet's own row number of the heading row, so a
// refused cell is named by the row the keeper sees.
export function buildRows(
  table,
  columns,
  { params, subtypeSlug, dateOrder = 'mdy', now, headerLine = 1, date1904 = false }
) {
  const [, ...body] = table;
  const di = columns.findIndex((c) => c.kind === 'date');
  const ti = columns.findIndex((c) => c.kind === 'time');
  const rows = [];
  const refused = [];
  body.forEach((r, idx) => {
    const rowLine = headerLine + idx + 1;
    const lineOf = (i) => table.cellLines?.[idx + 1]?.[i] ?? rowLine;
    const filled = columns.some((c, i) => c.kind === 'param' && !isBlank(r[i]));
    if (!filled) return;
    const when = di >= 0 ? withTime(r[di], ti >= 0 ? r[ti] : '', date1904) : '';
    // withTime has already turned a 1904 day number with a time into a
    // written date; only a bare day number still needs the shift.
    const date =
      di >= 0
        ? parseDateCell(when, dateOrder, { date1904: date1904 && /^\d{5}(\.\d+)?$/.test(when) })
        : null;
    const dateOnly = !hasTimeOfDay(String(when ?? ''));
    columns.forEach((c, i) => {
      if (c.kind !== 'param' || isBlank(r[i])) return;
      const raw = parseNumber(r[i], {
        thousands: THOUSANDS_PARAMS.has(c.param) || THOUSANDS_UNITS.has(c.unit),
      });
      if (raw == null) {
        const reason = /^\s*</.test(String(r[i]))
          ? 'Below what the test reads, so there is no number to save.'
          : 'Not a number.';
        refused.push({ line: lineOf(i), header: c.header, cell: r[i], reason });
        return;
      }
      if (!date) {
        refused.push({
          line: lineOf(i),
          header: c.header,
          cell: r[i],
          reason: 'The date could not be read.',
        });
        return;
      }
      // Fresh water keeps its own salinity number, as the readings route does.
      const fresh =
        c.param === 'salinity' && FRESHWATER_SUBTYPES.includes(String(subtypeSlug || ''));
      const opt = fresh ? null : (UNIT_OPTIONS[c.param] || []).find((o) => o.k === c.unit);
      const value = opt ? opt.to(raw) : raw;
      const reading = {
        param: c.param,
        value,
        ts: date.toISOString(),
        line: lineOf(i),
        date_only: dateOnly,
      };
      const shown =
        c.param === 'temp'
          ? `${raw}°${c.unit === 'c' ? 'C' : 'F'}`
          : c.param === 'salinity'
            ? String(raw)
            : null;
      const why = readingProblem(reading, { params, subtypeSlug, now, shown });
      if (why) refused.push({ line: lineOf(i), header: c.header, cell: r[i], reason: why });
      else rows.push(reading);
    });
  });
  return { rows, refused };
}
